import React, { useRef, useState } from "react";
import { Mic, Square, Loader2, Play, Pause, X } from "lucide-react";
import api from "../../lib/api";
import { authImageUrl } from "../../lib/storage";

/**
 * Voice recorder for chat — uses MediaRecorder, uploads to /api/uploads/voice,
 * returns transcript via Whisper.
 */
export default function VoiceRecorder({ onResult, onCancel }) {
  const [state, setState] = useState("idle"); // idle | recording | preview | uploading
  const [duration, setDuration] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const blobRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus" : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        blobRef.current = blob;
        setBlobUrl(URL.createObjectURL(blob));
        setState("preview");
      };
      mr.start();
      recorderRef.current = mr;
      setDuration(0);
      timerRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
      setState("recording");
    } catch (e) {
      alert("Microphone access denied");
    }
  };

  const stop = () => {
    clearInterval(timerRef.current);
    recorderRef.current?.stop();
  };

  const cancel = () => {
    clearInterval(timerRef.current);
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    blobRef.current = null;
    setBlobUrl(null);
    setState("idle");
    setDuration(0);
    onCancel?.();
  };

  const send = async () => {
    if (!blobRef.current) return;
    setState("uploading");
    const fd = new FormData();
    fd.append("file", blobRef.current, "voice.webm");
    try {
      const { data } = await api.post("/uploads/voice", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onResult?.(data);
      blobRef.current = null;
      setBlobUrl(null);
      setState("idle");
      setDuration(0);
    } catch (e) {
      alert(e.response?.data?.detail || "Upload failed");
      setState("preview");
    }
  };

  const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  if (state === "idle") {
    return (
      <button type="button" onClick={start} className="p-2.5 border border-white/10 hover:border-pitch hover:text-pitch transition-all" data-testid="voice-record-btn" title="Record voice">
        <Mic className="w-4 h-4" />
      </button>
    );
  }
  if (state === "recording") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded">
        <span className="live-dot" style={{ background: "#FF453A", boxShadow: "0 0 12px rgba(255,69,58,0.7)" }} />
        <span className="font-mono text-sm rec-blink">{mmss(duration)}</span>
        <span className="waveform text-red-400/90 mx-1">
          <i /><i /><i /><i /><i /><i /><i />
        </span>
        <button type="button" onClick={stop} className="text-pitch hover:text-white" data-testid="voice-stop" title="Stop">
          <Square className="w-4 h-4" />
        </button>
        <button type="button" onClick={cancel} className="text-white/40 hover:text-red-400" title="Cancel">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }
  if (state === "preview") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-pitch/10 border border-pitch/30 rounded">
        <audio src={blobUrl} controls className="h-8" style={{ maxWidth: 180 }} />
        <button type="button" onClick={send} className="btn-primary px-3 py-1.5 text-xs" data-testid="voice-send">SEND</button>
        <button type="button" onClick={cancel} className="text-white/40 hover:text-red-400" title="Discard"><X className="w-4 h-4" /></button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 px-3 py-2">
      <Loader2 className="w-4 h-4 animate-spin text-pitch" />
      <span className="font-mono text-xs text-white/60">Uploading...</span>
    </div>
  );
}
